import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// API's
import { getPayPlansAssigmentTree, getPayPlansByPositions } from "@/PayPlans/api/positions/queries";

// Helpers
import { hasViewAccess } from "@/PayPlans/helpers/Access";

// Subscriptions
import { getAssignmentsSubscriptions } from "./subscriptions";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useManagedPositions } from "@/Common/hooks/Positions";

// Layouts
import AssignmentsHistory from "@/PayPlans/Layouts/Historical";

// Custom Components
import AssignmentsHistoryContent from "@/PayPlans/components/Assignments/Contents/Historical";
import { retrievePositionsMap } from "@/Common/api/positions/queries";

const groupPayPlans = (payPlans = []) => {
	const positions = payPlans.map((item) => item.position);
	const uniquePositions = [...new Set(positions)];
	return uniquePositions.reduce((acc, position) => {
		const positionPayPlans = payPlans.filter((item) => item.position === position);
		const list = positionPayPlans.map((item) => ({
			_id: `${position}_${item.name}_${item._id}`,
			title: item.name,
			planId:item._id,
			position: item.position,
			createdAt: item.createdAt,
			updatedAt: item.updatedAt,
			createdBy: item.createdBy,
			updatedBy: item.updatedBy,
		}));
		acc[position] = list;
		return acc;
	}, {});
};

export default function Group() {
	const [selected, setSelected] = useState(null);
	const location = useSelector((state) => state.location.location);
	const { isPresentAccess } = useAuthorization();
	const { clearCache } = useCacheCleaner();
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const handleSelection = (selection) => {
		if (selection) {
			addSubscription(
				["groupPayPlanAssignmentUsersByPosition", location?.locationId, selection.position, selection.title],
				getAssignmentsSubscriptions()
			);
			setSelected(selection);
		}
	};

	// Queries
	const { data: typePositionsSettings, isLoading: isLoadingPositionTypes } = useQuery({
		queryKey: ["typePositionsSettings", location?.locationId],
		queryFn: async () => retrievePositionsMap(location?.locationId),
		enabled: !!location?.locationId,
		staleTime: Infinity,
	});

	const { data: tree, isLoading } = useQuery({
		queryKey: ["getPayPlansAssigmentTree", location?.locationId],
		queryFn: async () => {
			let positionsList = [];
			typePositionsSettings.map((item, index) => {
				positionsList.push({ position: item.systemPosition, isDepartmental: item.isDepartmental });
			});
			const payPlans = await getPayPlansAssigmentTree(location?.locationId, positionsList);
			const grouped = groupPayPlans(payPlans);
			return Object.entries(grouped)?.map(([position, value]) => ({
				_id: position,
				title: position,
				bold: true,
				disabled: !value.length,
				list: value,
				position,
			}));
		},
		enabled: !!location?.locationId && !!typePositionsSettings?.length,
		staleTime: Infinity,
	});

	useEffect(() => {
		if (!location) {
			return;
		}
		return () => {
			setSelected(null);
			clearSubscriptionContext();
		};
	}, [location, clearSubscriptionContext]);

	useEffect(() => {
		return () => {
			clearCache(["assignSelectedPlanButton"]);
		};
	}, [clearCache]);

	return (
		<AssignmentsHistory
			tree={tree}
			selected={selected}
			loading={isLoading || isLoadingPositionTypes}
			isLocationAvailable={Boolean(location)}
			emptyTitle={"No Pay Plan selected"}
			onActionSelected={handleSelection}
		>
			<AssignmentsHistoryContent selectedItem={selected} />
		</AssignmentsHistory>
	);
}
